import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, Tag, Search, Filter, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EventsPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('pt') ? 'en' : i18n.language?.startsWith('en') ? 'en' : 'es';
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const categories = [
    { value: '', label: 'Todas las categorías' },
    { value: 'concierto', label: 'Conciertos' },
    { value: 'festival', label: 'Festivales' },
    { value: 'privado', label: 'Eventos Privados' },
    { value: 'corporativo', label: 'Corporativos' },
    { value: 'experiencia', label: 'Experiencias' }
  ];

  useEffect(() => {
    fetchEvents();
  }, [selectedCategory, selectedCity]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCity) params.append('city', selectedCity);
      params.append('upcoming', 'true');
      
      const response = await axios.get(`${API}/api/public/events?${params.toString()}`);
      setEvents(response.data.items || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getLowestPrice = (accessLevels) => {
    if (!accessLevels || accessLevels.length === 0) return null;
    const prices = accessLevels.map(l => l.price).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const filteredEvents = events.filter(event => {
    const name = event.name?.[lang] || event.name?.es || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                Eventos
              </span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Descubre experiencias únicas curadas por GS Agency. Eventos exclusivos que marcan la diferencia.
            </p>
          </motion.div>

          {/* Search and Filters */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar eventos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-zinc-800 text-white pl-12 pr-4 py-3 rounded-xl border border-zinc-700 focus:border-amber-500 focus:outline-none transition-colors"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="bg-zinc-800 text-white px-4 py-3 rounded-xl border border-zinc-700 focus:border-amber-500 focus:outline-none transition-colors"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-zinc-900 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-zinc-800" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-zinc-800 rounded w-3/4" />
                    <div className="h-4 bg-zinc-800 rounded w-1/2" />
                    <div className="h-4 bg-zinc-800 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Calendar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No hay eventos disponibles</h3>
              <p className="text-gray-500">Próximamente nuevas experiencias exclusivas</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map((event, index) => {
                const lowestPrice = getLowestPrice(event.access_levels);
                const availability = event.total_capacity - (event.total_sold || 0);
                
                return (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link 
                      to={`/eventos/${event.slug}`}
                      className="group block bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 hover:border-amber-500/50 transition-all duration-300"
                    >
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        {event.cover_image ? (
                          <img 
                            src={event.cover_image} 
                            alt={event.name?.[lang] || event.name?.es}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center">
                            <Calendar className="w-12 h-12 text-amber-500/50" />
                          </div>
                        )}
                        
                        {/* Category Badge */}
                        {event.category && (
                          <span className="absolute top-4 left-4 bg-amber-500/90 text-black text-xs font-bold px-3 py-1 rounded-full uppercase">
                            {event.category}
                          </span>
                        )}
                        
                        {/* Price Badge */}
                        {lowestPrice && (
                          <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg">
                            <span className="text-amber-400 font-bold">${lowestPrice.toLocaleString()}</span>
                            <span className="text-gray-400 text-sm ml-1">{event.access_levels[0]?.currency || 'MXN'}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors line-clamp-2">
                          {event.name?.[lang] || event.name?.es || 'Evento'}
                        </h3>
                        
                        <div className="space-y-2 text-sm text-gray-400">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                            <span className="capitalize">{formatDate(event.date)}</span>
                          </div>
                          
                          {event.start_time && (
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-amber-500" />
                              <span>{event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}</span>
                            </div>
                          )}
                          
                          {event.venue_name && (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-amber-500" />
                              <span className="line-clamp-1">{event.venue_name}</span>
                            </div>
                          )}
                          
                          {event.total_capacity > 0 && (
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-2 text-amber-500" />
                              <span>
                                {availability > 0 ? (
                                  <>{availability} lugares disponibles</>
                                ) : (
                                  <span className="text-red-400">Agotado</span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* CTA */}
                        <div className="mt-6 flex items-center justify-between">
                          <span className="text-amber-400 font-medium group-hover:text-amber-300 transition-colors">
                            Ver detalles
                          </span>
                          <ChevronRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
